// ProfilePage — /profile.
//
// Lets the current staff user edit their own profile (name, email, phone,
// address, postcode) and change their password. Mirrors the legacy
// `tricivo_system/operations/edit_user.php` for the current user only —
// admin-on-other-user edits live under /admin/users/:id/edit.
//
// Reads the current user from useAuthStore (populated via /auth/me at app
// boot), fetches the full staff record via /api/v1/users/:uuid, and PUTs
// updates to the same path. Cache invalidates ['users', 'me'].

import { useEffect, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, KeyRound, Save } from 'lucide-react';
import { PageHeader } from '../../components/ui/PageHeader/PageHeader';
import { Card } from '../../components/ui/Card/Card';
import { Button } from '../../components/ui/Button/Button';
import { Input } from '../../components/form/Input';
import { Textarea } from '../../components/form/Textarea';
import { Spinner } from '../../components/ui/Spinner/Spinner';
import { apiClient } from '../../api/client';
import { endpoints } from '../../api/endpoints';
import { useToast } from '../../hooks/useToast';
import { useAuthStore } from '../../store/authStore';
import { KioskPinSetup } from '../weighbridge/kiosk/KioskPinSetup';
import type { StaffSummary, UpdateStaffInput } from '../../types/api';
import styles from './ProfilePage.module.css';

const ME_KEY = ['users', 'me'] as const;

interface ProfileForm {
  name: string;
  email: string;
  phone: string;
  address: string;
  postcode: string;
}

interface PasswordForm {
  current_password: string;
  new_password: string;
  confirm_password: string;
}

const EMPTY_PROFILE: ProfileForm = {
  name: '',
  email: '',
  phone: '',
  address: '',
  postcode: '',
};

const EMPTY_PASSWORD: PasswordForm = {
  current_password: '',
  new_password: '',
  confirm_password: '',
};

export function ProfilePage(): JSX.Element {
  const navigate = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const updateUser = useAuthStore((s) => s.updateUser);

  const meQuery = useQuery<StaffSummary>({
    queryKey: ME_KEY,
    queryFn: async () => {
      if (!user?.uuid) {
        throw new Error('Not authenticated');
      }
      const res = await apiClient.get<{ staff: StaffSummary }>(
        endpoints.users.detail(user.uuid),
      );
      return res.data.staff;
    },
    enabled: !!user?.uuid,
    staleTime: 60_000,
  });

  const [profile, setProfile] = useState<ProfileForm>(EMPTY_PROFILE);
  const [password, setPassword] = useState<PasswordForm>(EMPTY_PASSWORD);

  useEffect(() => {
    if (!meQuery.data) return;
    const m = meQuery.data;
    setProfile({
      name: m.name ?? '',
      email: m.email ?? '',
      phone: m.phone ?? '',
      address: m.address ?? '',
      postcode: m.postcode ?? '',
    });
  }, [meQuery.data]);

  const profileMutation = useMutation({
    mutationFn: async (input: UpdateStaffInput) => {
      if (!user?.uuid) throw new Error('Not authenticated');
      const res = await apiClient.put<{ staff: StaffSummary }, UpdateStaffInput>(
        endpoints.users.update(user.uuid),
        input,
      );
      return res.data.staff;
    },
    onSuccess: (data) => {
      updateUser({ name: data.name, email: data.email });
      void qc.invalidateQueries({ queryKey: ME_KEY });
      toast.success('Profile updated.');
    },
    onError: () => {
      toast.error('Failed to update profile.');
    },
  });

  const passwordMutation = useMutation({
    mutationFn: async (input: UpdateStaffInput) => {
      if (!user?.uuid) throw new Error('Not authenticated');
      await apiClient.put<{ staff: StaffSummary }, UpdateStaffInput>(
        endpoints.users.update(user.uuid),
        input,
      );
    },
    onSuccess: () => {
      setPassword(EMPTY_PASSWORD);
      toast.success('Password changed. Other sessions may need to log in again.');
    },
    onError: () => {
      toast.error('Failed to change password. Check your current password.');
    },
  });

  function handleProfileSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    profileMutation.mutate({
      name: profile.name,
      email: profile.email,
      phone: profile.phone || null,
      address: profile.address || null,
      postcode: profile.postcode || null,
    });
  }

  function handlePasswordSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (password.new_password.length < 8) {
      toast.error('New password must be at least 8 characters.');
      return;
    }
    if (password.new_password !== password.confirm_password) {
      toast.error('New passwords do not match.');
      return;
    }
    passwordMutation.mutate({
      current_password: password.current_password,
      new_password: password.new_password,
    });
  }

  if (meQuery.isLoading || !meQuery.data) {
    return (
      <div className={styles.page}>
        <PageHeader title="My Profile" />
        <Card>
          <div className={styles.empty}>
            <Spinner />
          </div>
        </Card>
      </div>
    );
  }

  const me = meQuery.data;
  return (
    <div className={styles.page}>
      <PageHeader
        title="My Profile"
        subtitle="Update your account details and password"
        actions={
          <Button
            variant="ghost"
            icon={ArrowLeft}
            onClick={() => navigate('/collections/dashboard')}
          >
            Back
          </Button>
        }
      />

      <Card title="Account">
        <ul className={styles.metaList}>
          <li>
            <span>Username</span>
            <strong>{me.username}</strong>
          </li>
          <li>
            <span>Role</span>
            <strong>{me.role.replace('_', ' ')}</strong>
          </li>
          <li>
            <span>Status</span>
            <strong>{me.status}</strong>
          </li>
          {me.last_login_at ? (
            <li>
              <span>Last login</span>
              <strong>{new Date(me.last_login_at).toLocaleString('en-GB')}</strong>
            </li>
          ) : null}
        </ul>
      </Card>

      <Card title="Profile">
        <form onSubmit={handleProfileSubmit} className={styles.form}>
          <div className={styles.row}>
            <Input
              label="Name"
              value={profile.name}
              onChange={(e) => setProfile((p) => ({ ...p, name: e.target.value }))}
              required
            />
            <Input
              label="Email"
              variant="email"
              value={profile.email}
              onChange={(e) => setProfile((p) => ({ ...p, email: e.target.value }))}
              required
            />
          </div>
          <div className={styles.row}>
            <Input
              label="Phone"
              variant="tel"
              value={profile.phone}
              onChange={(e) => setProfile((p) => ({ ...p, phone: e.target.value }))}
            />
            <Input
              label="Postcode"
              value={profile.postcode}
              onChange={(e) =>
                setProfile((p) => ({ ...p, postcode: e.target.value }))
              }
            />
          </div>
          <Textarea
            label="Address"
            value={profile.address}
            onChange={(e) => setProfile((p) => ({ ...p, address: e.target.value }))}
            rows={3}
          />
          <div className={styles.formActions}>
            <Button
              type="submit"
              variant="primary"
              icon={Save}
              loading={profileMutation.isPending}
            >
              Save profile
            </Button>
          </div>
        </form>
      </Card>

      <Card title="Change password">
        <form onSubmit={handlePasswordSubmit} className={styles.form}>
          <Input
            label="Current password"
            variant="password"
            value={password.current_password}
            onChange={(e) =>
              setPassword((p) => ({ ...p, current_password: e.target.value }))
            }
            required
            autoComplete="current-password"
          />
          <Input
            label="New password"
            variant="password"
            value={password.new_password}
            onChange={(e) =>
              setPassword((p) => ({ ...p, new_password: e.target.value }))
            }
            required
            autoComplete="new-password"
            helper="Minimum 8 characters."
          />
          <Input
            label="Confirm new password"
            variant="password"
            value={password.confirm_password}
            onChange={(e) =>
              setPassword((p) => ({ ...p, confirm_password: e.target.value }))
            }
            required
            autoComplete="new-password"
          />
          <div className={styles.formActions}>
            <Button
              type="submit"
              variant="primary"
              icon={KeyRound}
              loading={passwordMutation.isPending}
            >
              Change password
            </Button>
          </div>
        </form>
      </Card>

      {/* Kiosk PIN — weighbridge operators only use the kiosk and never reach
          this page, so the section is hidden for that role. */}
      {me.role !== 'weighbridge_operator' ? (
        <Card title="Kiosk PIN">
          <KioskPinSetup />
        </Card>
      ) : null}
    </div>
  );
}
